import { h, Fragment } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { RoomItem } from './RoomItem.jsx'
import { FloatPanel } from './FloatPanel.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

const DEBOUNCE_MS = 400

/**
 * DockApp — Preact root for the chat dock popup.
 *
 * Renders the room list in dock mode. When a room is clicked, it opens
 * a FloatPanel instead of navigating to the full-screen chat.
 * Float panels are managed via store.floatPanels signal.
 *
 * Props:
 *   store    — createChatStore() instance (no active room required)
 *   cable    — connectChatCable() return value
 *   personId — current person id (Number)
 */
export function DockApp({ store, cable, personId, personName = '' }) {
  // Load room list on mount
  useEffect(() => {
    store.roomsLoading.value = true
    chatApi.rooms.list().then(async res => {
      if (res.ok) {
        const data = await res.json
        store.rooms.value = Array.isArray(data) ? data : (data.rooms ?? [])
      }
    }).catch(() => { /* ignore */ })
      .finally(() => { store.roomsLoading.value = false })
  }, []) // eslint-disable-line

  // Persist float panel state to localStorage so panels survive page refresh.
  // Runs in DockApp (always mounted) to guarantee the final write executes even
  // when the last panel is closed and its FloatPanel unmounts before the effect fires.
  useEffect(() => {
    if (!personId) return
    const key = `tsh:chatDock:${personId}.panels`
    const panels = store.floatPanels.value
    try {
      if (panels.length === 0) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, JSON.stringify(panels))
      }
    } catch { /* ignore */ }
  }, [store.floatPanels.value]) // eslint-disable-line

  const floatPanels = store.floatPanels.value

  function handleRoomClick(room) {
    const already = store.floatPanels.value.some(p => p.roomId === room.id)
    if (!already) {
      store.floatPanels.value = [...store.floatPanels.value, { roomId: room.id, minimized: false }]
    } else {
      // Un-minimize if it was already open but minimized
      store.floatPanels.value = store.floatPanels.value.map(p =>
        p.roomId === room.id ? { ...p, minimized: false } : p
      )
    }
    // Close the dock popup after opening a room
    const dockEl = document.getElementById('chat-dock')
    if (dockEl) {
      dockEl.classList.remove('is-open')
      const win = dockEl.querySelector('[data-chat-dock-target="window"]')
      if (win) win.classList.remove('chat-dock-window--open')
      const btn = dockEl.querySelector('[data-chat-dock-target="toggle"]')
      if (btn) btn.setAttribute('aria-expanded', 'false')
    }
  }

  return (
    <>
      {/* Dock room list with click-to-float-panel handler */}
      <DockRoomList store={store} cable={cable} onRoomClick={handleRoomClick} />

      {/* Float panels spawned from the dock — each gets its own isolated store */}
      {floatPanels.map((panel, idx) => (
        <FloatPanel
          key={panel.roomId}
          dockStore={store}
          panelRoomId={panel.roomId}
          personId={personId}
          personName={store.personName}
          tenantId={store.tenantId}
          panelIndex={idx}
          minimized={panel.minimized}
        />
      ))}
    </>
  )
}

/**
 * DockRoomList — room list rendered inside the dock popup.
 *
 * Mirrors RoomList's search/filter behaviour but routes clicks to onRoomClick
 * (which opens a FloatPanel) instead of setting activeRoomId.
 *
 * Props: { store, cable, onRoomClick }
 */
function DockRoomList({ store, cable, onRoomClick }) {
  const searchText    = useSignal('')
  const searchResults = useSignal(null)
  const debounceTimer = useRef(null)

  const rooms        = store.rooms.value
  const floatPanels  = store.floatPanels.value
  const roomsLoading = store.roomsLoading.value

  useEffect(() => {
    const q = searchText.value.trim()
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    if (!q) {
      searchResults.value = null
      return
    }
    debounceTimer.current = setTimeout(() => {
      chatApi.search.rooms(q).then(async res => {
        if (res.ok) {
          const data = await res.json
          searchResults.value = Array.isArray(data) ? data : (data.rooms ?? [])
        }
      }).catch(() => { /* ignore */ })
    }, DEBOUNCE_MS)
  }, [searchText.value]) // eslint-disable-line

  const q = searchText.value.trim().toLowerCase()
  const displayRooms = searchResults.value != null
    ? searchResults.value
    : q
      ? rooms.filter(r => (r.name || r.display_name || '').toLowerCase().includes(q))
      : rooms

  return (
    <div class="chat-dock-inner">
      {/* Search input */}
      <div class="chat-sidebar-search chat-dock-search">
        <i class="bi bi-search chat-sidebar-search-icon"></i>
        <input
          type="search"
          class="chat-sidebar-search-input"
          placeholder={t('chat.room_search_placeholder')}
          value={searchText.value}
          onInput={e => { searchText.value = e.target.value }}
        />
        {searchText.value && (
          <button
            type="button"
            class="chat-sidebar-search-clear"
            onClick={() => { searchText.value = ''; searchResults.value = null }}
          >
            <i class="bi bi-x"></i>
          </button>
        )}
      </div>

      {/* Room list */}
      <div class="chat-dock-rooms-list">
        {roomsLoading && rooms.length === 0 ? (
          <div class="chat-rooms-loader">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : displayRooms.length === 0 && q ? (
          <div class="chat-sidebar-empty">{t('chat.search_no_results')}</div>
        ) : displayRooms.length === 0 && !roomsLoading ? (
          <div class="chat-dock-empty">
            <i class="bi bi-chat-square-dots chat-dock-empty-icon"></i>
            <p>{t('chat.dock.no_rooms_empty')}</p>
          </div>
        ) : (
          displayRooms.map(room => (
            <RoomItem
              key={room.id}
              room={room}
              store={store}
              cable={cable}
              active={floatPanels.some(p => p.roomId === room.id)}
              onClick={() => onRoomClick(room)}
            />
          ))
        )}
      </div>
    </div>
  )
}
